import React, { useState } from 'react';
import { Modal, Form, Select, Input, Alert, Radio, Space, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

const REASONS = [
    'speeding',
    'straight_lining',
    'pattern_response',
    'failed_attention_check',
    'inconsistent_responses',
    'duplicate_fingerprint',
    'suspicious_device',
    'low_quality_text',
    'other_quality_concern',
];

const { Text } = Typography;

export type BulkRejectMode = 'smart' | 'single';

interface BulkRejectDialogProps {
    open: boolean;
    submitting: boolean;
    selectedCount: number;
    onCancel: () => void;
    onSubmit: (mode: BulkRejectMode, reason?: string, notes?: string) => Promise<void> | void;
}

export const BulkRejectDialog: React.FC<BulkRejectDialogProps> = ({
    open,
    submitting,
    selectedCount,
    onCancel,
    onSubmit,
}) => {
    const intl = useIntl();
    const [mode, setMode] = useState<BulkRejectMode>('smart');
    const [reason, setReason] = useState<string | undefined>(undefined);
    const [notes, setNotes] = useState<string>('');

    const reset = () => {
        setMode('smart');
        setReason(undefined);
        setNotes('');
    };

    const canSubmit = mode === 'smart' || (mode === 'single' && !!reason);

    const handleOk = async () => {
        if (!canSubmit) return;
        await onSubmit(mode, mode === 'single' ? reason : undefined, notes || undefined);
        reset();
    };

    const handleCancel = () => {
        reset();
        onCancel();
    };

    return (
        <Modal
            title={
                <FormattedMessage
                    id="responseIntegrity.bulkReject.title"
                    values={{ count: selectedCount }}
                />
            }
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            okText={intl.formatMessage(
                { id: 'responseIntegrity.bulkReject.submit' },
                { count: selectedCount },
            )}
            okButtonProps={{ danger: true, disabled: !canSubmit, loading: submitting }}
            cancelText={intl.formatMessage({ id: 'common.cancelButton' })}
            width={560}
        >
            <Alert
                type="warning"
                showIcon
                message={
                    <FormattedMessage
                        id="responseIntegrity.bulkReject.intro"
                        values={{ count: selectedCount }}
                    />
                }
                style={{ marginBottom: 16 }}
            />
            <Form layout="vertical">
                <Form.Item label={<FormattedMessage id="responseIntegrity.bulkReject.modeLabel" />}>
                    <Radio.Group
                        value={mode}
                        onChange={(e) => setMode(e.target.value)}
                    >
                        <Space direction="vertical">
                            <Radio value="smart">
                                <FormattedMessage id="responseIntegrity.bulkReject.modeSmart" />
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginLeft: 24 }}>
                                    <FormattedMessage id="responseIntegrity.bulkReject.modeSmartHint" />
                                </Text>
                            </Radio>
                            <Radio value="single">
                                <FormattedMessage id="responseIntegrity.bulkReject.modeSingle" />
                                <Text type="secondary" style={{ display: 'block', fontSize: 12, marginLeft: 24 }}>
                                    <FormattedMessage id="responseIntegrity.bulkReject.modeSingleHint" />
                                </Text>
                            </Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
                {mode === 'single' && (
                    <Form.Item
                        label={<FormattedMessage id="responseIntegrity.rejectDialog.reasonLabel" />}
                        required
                    >
                        <Select
                            value={reason}
                            onChange={setReason}
                            placeholder={intl.formatMessage({
                                id: 'responseIntegrity.rejectDialog.reasonPlaceholder',
                            })}
                            options={REASONS.map((code) => ({
                                value: code,
                                label: intl.formatMessage({ id: `responseIntegrity.reasons.${code}` }),
                            }))}
                        />
                    </Form.Item>
                )}
                <Form.Item label={<FormattedMessage id="responseIntegrity.rejectDialog.notesLabel" />}>
                    <Input.TextArea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        placeholder={intl.formatMessage({
                            id: 'responseIntegrity.rejectDialog.notesPlaceholder',
                        })}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};
